import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Dropdown, Drawer, Badge, Popover } from 'antd';
import type { MenuProps } from 'antd';
import { UserOutlined, MenuOutlined, CloseOutlined, BellOutlined } from '@ant-design/icons';
import logoSmall from '@assets/images/logoSmall.svg';
import { useAppDispatch, useAppSelector } from '@hooks/redux';
import ruFlag from '@assets/images/ru.png';
import tjFlag from '@assets/images/tj.png';
import usaFlag from '@assets/images/usa.png';
import zhFlag from '@assets/images/zh.png';
import { logOutApi } from '@http/auth';
import { markNotificationsReadApi } from '@http/user';
import { ShieldCheck } from 'lucide-react';

const NOTIFICATION_LANGUAGE_KEYS: Record<string, string> = { tg: 'tj' };

const getNotificationMessage = (message: any, language: string) => {
  if (typeof message === 'string') {
    return message;
  }
  return message?.[NOTIFICATION_LANGUAGE_KEYS[language] ?? language] ?? message?.ru;
};

function Header() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const [isSideBarOpen, setisOpenSideBar] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const user = useAppSelector(state => state.appReducer.user);
  const transactionAlert = useAppSelector(state => state.globalReducer.transactionAlert);
  const permissions = useAppSelector(state => state.userReducer.permissions);
  const notifications = useAppSelector(state => state.userReducer.notifications);

  const notificationsList = Array.isArray(notifications) ? notifications : notifications?.results || [];
  const unreadCount = notificationsList.filter((item: any) => !item.is_read).length;
  const [highlightedIds, setHighlightedIds] = useState<number[]>([]);

  const handleNotificationsOpenChange = (open: boolean) => {
    setIsNotificationsOpen(open);
    if (open) {
      const unreadIds = notificationsList.filter((item: any) => !item.is_read).map((item: any) => item.id);
      setHighlightedIds(unreadIds);
      if (unreadIds.length) {
        dispatch(markNotificationsReadApi(unreadIds));
      }
    }
  };

  let routes: any = [];
  const staticRoutes = [
    {
      key: 'accounts',
      path: '/accounts/bankAccounts',
      label: t('Accounts'),
    },
    {
      key: 'transactions',
      path: '/transactions',
      label: t('Transactions'),
    },
  ];

  const permissionsRoutes = [
    {
      key: 'payments',
      path: '/payments/own',
      label: t('Payments'),
    },
    {
      key: 'salary',
      path: '/salary/list',
      label: t('Salaries'),
    },
    {
      key: 'statement',
      path: '/statement',
      label: t('Statement'),
    },
  ];

  const checkRoutes = () => {
    routes = [...staticRoutes];
    for (let i = 0; i < permissionsRoutes.length; i++) {
      for (const key in permissions) {
        if (permissionsRoutes[i].key === key) {
          routes.push(permissionsRoutes[i]);
        }
      }
    }
  };

  checkRoutes();

  const items: MenuProps['items'] = [
    ...(permissions.deposits
      ? [
          {
            key: '1',
            label: t('Deposits'),
            onClick: () => {
              navigate('/deposits');
            },
          },
        ]
      : []),
    ...(permissions.credits
      ? [
          {
            key: '2',
            label: t('Loans'),
            onClick: () => {
              navigate('/loans');
            },
          },
        ]
      : []),
    ...(permissions.cpty
      ? [
          {
            key: '3',
            label: t('Counterparties'),
            onClick: () => {
              navigate('/counterparties');
            },
          },
        ]
      : []),

    {
      key: '4',
      label: t('Tariffs'),
      onClick: () => {
        navigate('/commissions');
      },
    },
    {
      key: '5',
      label: t('Exchange Rates'),
      onClick: () => {
        navigate('/rates');
      },
    },
    {
      key: '6',
      label: t('Templates'),
      onClick: () => {
        navigate('/templates');
      },
    },
  ];

  const items2: MenuProps['items'] = [
    {
      key: '1',
      label: user.position,
    },
    {
      key: '2',
      label: t('Profile'),
      onClick: () => {
        navigate('/profile');
      },
    },
    {
      key: '3',
      danger: true,
      label: t('Log out'),
      onClick: () => {
        dispatch(logOutApi());
      },
    },
  ];

  const languages: MenuProps['items'] = [
    {
      key: '1',
      label: 'Русский',
      onClick: () => {
        i18n.changeLanguage('ru');
        localStorage.setItem('lang', 'ru');
      },
    },
    {
      key: '2',
      label: 'Таджикский',
      onClick: () => {
        i18n.changeLanguage('tg');
        localStorage.setItem('lang', 'tg');
      },
    },
    {
      key: '3',
      label: 'English',
      onClick: () => {
        i18n.changeLanguage('en');
        localStorage.setItem('lang', 'en');
      },
    },
  ];

  const getLanguageIcon = (language: string) => {
    switch (language) {
      case 'ru':
        return ruFlag;
      case 'tg':
        return tjFlag;
      case 'en':
        return usaFlag;
      case 'zh':
        return zhFlag;

      default:
        return ruFlag;
    }
  };

  const navigateHandler = (url: string) => {
    setisOpenSideBar(false);
    navigate(url);
  };

  const checkActiveRoute = (route: any) => {
    if (location.pathname.includes(route)) {
      return 'header__items-active';
    }
    return null;
  };

  return (
    <div className='max-lg:w-full px-5'>
      <header className='flex justify-between lg:w-[1130px] h-20 items-center'>
        <Link to='/' className='flex items-center'>
          <img src={logoSmall} alt='' className='w-10 h-10' />
          <div className='text-white font-semibold ml-3'>
            <p className='md:text-2xl text-xl leading-4 mt-1 color-active'>{t('Oriyon')}</p>
            <p className={`${i18n.language === 'en' ? 'mt-0.5' : ''} text-xs md:text-sm text-right color-active`}>
              {t('Business')}
            </p>
          </div>
        </Link>

        <div className='max-md:hidden w-1/2 h-full'>
          <div
            className={`${
              Object.keys(permissions).length > 1 ? 'justify-between' : 'justify-around'
            } flex  font-medium items-center h-full`}
            style={{ color: '#fff !important' }}
          >
            {routes.map((item: any) => (
              <div className='relative h-full' key={item.key}>
                <Link
                  to={item.path}
                  className={`header__items ${checkActiveRoute(
                    item.key
                  )} h-full flex items-center color-active text-sm font-medium`}
                >
                  {item.label}
                </Link>
                {item.key === 'transactions' && transactionAlert ? (
                  <div className='bg-green-500 h-2 w-2 rounded-full absolute' style={{ top: '17px', right: '-7px' }} />
                ) : null}
              </div>
            ))}
            <Dropdown
              menu={{
                items,
              }}
              trigger={['click']}
              placement='bottomLeft'
              className='cursor-pointer'
            >
              <span className='header__items py-5 text-sm font-medium'>{t('More')}</span>
            </Dropdown>
          </div>
        </div>
        <div className='flex items-center'>
          <Dropdown
            menu={{
              items: languages,
            }}
            trigger={['click']}
            placement='bottomRight'
            className='cursor-pointer'
          >
            <div className='flex items-center'>
              <img src={getLanguageIcon(i18n.language)} alt='' className='w-8 h-8' />
            </div>
          </Dropdown>

          <Popover
            trigger='click'
            placement='bottomRight'
            open={isNotificationsOpen}
            onOpenChange={handleNotificationsOpenChange}
            title={<p className='text-base text-center'>{t('Notifications')}</p>}
            overlayClassName='notificationsPopover'
            overlayInnerStyle={{ width: 340, maxWidth: '100%' }}
            content={
              <div className='max-h-[60vh] overflow-y-auto'>
                {notificationsList.length ? (
                  notificationsList.map((item: any) => (
                    <div
                      key={item.id}
                      className={`mb-2 rounded-lg border p-3 ${
                        highlightedIds.includes(item.id) ? 'bg-slate-100 border-blue-200' : 'bg-gray-50'
                      }`}
                    >
                      <p className='font-semibold text-[#011957] text-base'>{item.notification}</p>
                      <p className='mt-1 text-gray-600 text-base'>
                        {getNotificationMessage(item.message, i18n.language)}
                      </p>
                    </div>
                  ))
                ) : (
                  <p className='text-center text-gray-400'>{t('No notifications')}</p>
                )}
              </div>
            }
          >
            <Badge count={unreadCount} size='small' offset={[-2, 2]} className='ml-4'>
              <BellOutlined style={{ fontSize: '22px', color: '#011957' }} className='cursor-pointer' />
            </Badge>
          </Popover>

          <ShieldCheck
            size={24}
            color='#011957'
            className='cursor-pointer ml-4'
            onClick={() => navigate('/security')}
          />

          <div className='max-md:hidden'>
            <Dropdown
              menu={{
                items: items2,
              }}
              trigger={['click']}
              placement='bottomRight'
              className='cursor-pointer ml-4'
            >
              <UserOutlined style={{ fontSize: '22px', color: '#011957' }} />
            </Dropdown>
          </div>

          <div className='md:hidden ml-4'>
            {isSideBarOpen ? (
              <CloseOutlined onClick={() => setisOpenSideBar(false)} style={{ fontSize: '24px', color: '#011957' }} />
            ) : (
              <MenuOutlined onClick={() => setisOpenSideBar(true)} style={{ fontSize: '24px', color: '#011957' }} />
            )}
          </div>
        </div>
      </header>
      <Drawer
        className='color-active font-semibold'
        closable={false}
        title={null}
        placement='right'
        open={isSideBarOpen}
      >
        <div className='border-b'>
          <div
            className='py-3 text-base'
            onClick={() => navigateHandler('accounts/bankAccounts')}
            role='button'
            tabIndex={0}
          >
            {t('Accounts')}
          </div>
        </div>
        <div className='border-b'>
          <div
            className='py-3 text-base'
            onClick={() => navigateHandler('transactions/all')}
            role='button'
            tabIndex={0}
          >
            {t('Transactions')}
          </div>
        </div>
        <div className='border-b'>
          <div className='py-3 text-base' onClick={() => navigateHandler('payments/own')} role='button' tabIndex={0}>
            {t('Payments')}
          </div>
        </div>
        <div className='border-b'>
          <div className='py-3 text-base' onClick={() => navigateHandler('salary/list')} role='button' tabIndex={0}>
            {t('Salaries')}
          </div>
        </div>
        <div className='border-b'>
          <div className='py-3 text-base' onClick={() => navigateHandler('statement')} role='button' tabIndex={0}>
            {t('Statement')}
          </div>
        </div>

        <div className='border-b'>
          <div className='py-3 text-base' onClick={() => navigateHandler('loans')} role='button' tabIndex={0}>
            {t('Loans')}
          </div>
        </div>
        <div className='border-b'>
          <div className='py-3 text-base' onClick={() => navigateHandler('deposits')} role='button' tabIndex={0}>
            {t('Deposits')}
          </div>
        </div>
        <div className='border-b'>
          <div className='py-3 text-base' onClick={() => navigateHandler('commissions')} role='button' tabIndex={0}>
            {t('Tariffs')}
          </div>
        </div>

        <div className='border-b'>
          <div className='py-3 text-base' onClick={() => navigateHandler('rates')} role='button' tabIndex={0}>
            {t('Currency')}
          </div>
        </div>

        <div className='border-b'>
          <div className='py-3 text-base' onClick={() => navigateHandler('templates')} role='button' tabIndex={0}>
            {t('Templates')}
          </div>
        </div>

        <div className='border-b'>
          <div className='py-3 text-base' onClick={() => dispatch(logOutApi())} role='button' tabIndex={0}>
            {t('Exit')}
          </div>
        </div>
      </Drawer>
    </div>
  );
}

export default Header;
